import { useState } from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { PublicShell } from "@/components/PublicShell"
import { Button } from "@/components/ui/button"
import { EntityCombobox } from "@/components/EntityCombobox"

type MemberFormProps = {
  id: number | null
  band_id: number | null
  band_name: string | null
  person_id: number | null
  person_name: string | null
  details: string | null
  joined_year: number | null
  left_year: number | null
  importance: number
  instruments: string[]
}
type PageProps = { member: MemberFormProps; return_to?: string | null; errors?: Record<string, string> }

const ic = "w-full rounded border border-hairline bg-page px-3 py-2 text-sm text-ink-body focus:outline-none focus:ring-1 focus:ring-primary"

export default function MemberForm({ member, return_to }: { member: MemberFormProps; return_to?: string | null }) {
  const { errors = {} } = usePage<PageProps>().props
  const isNew = member.id === null
  const [form, setForm] = useState({
    band_id:     member.band_id !== null ? String(member.band_id) : "",
    band_name:   member.band_name ?? "",
    person_id:   member.person_id !== null ? String(member.person_id) : "",
    person_name: member.person_name ?? "",
    details:     member.details ?? "",
    joined_year: member.joined_year !== null ? String(member.joined_year) : "",
    left_year:   member.left_year !== null ? String(member.left_year) : "",
    importance:  String(member.importance ?? 0),
    instruments: member.instruments.join(", "),
  })

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const payload = return_to ? { member: form, return_to } : { member: form }
    if (isNew) router.post("/members", payload)
    else       router.patch(`/members/${member.id}`, payload)
  }

  const cancelHref = return_to ?? "/"
  const title = isNew ? "New member" : "Edit member"

  return (
    <>
      <Head title={title}>
        <meta name="description" content={title} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={title} />
      </Head>
      <PublicShell>
        <div className="border-b border-hairline pb-6">
          <h1>{title}</h1>
        </div>

        <form onSubmit={handleSubmit} className="mt-6 max-w-xl space-y-4">
          <div>
            <label className="block text-sm text-ink-muted mb-1">Band</label>
            <EntityCombobox
              type="Band"
              initialId={form.band_id}
              initialName={form.band_name}
              placeholder="Search bands…"
              onSelect={(id, name) => setForm((prev) => ({ ...prev, band_id: id, band_name: name }))}
              inputClass={ic}
            />
            {errors.band && <p className="mt-1 text-xs text-red-600">{errors.band}</p>}
          </div>

          <div>
            <label className="block text-sm text-ink-muted mb-1">Person</label>
            <EntityCombobox
              type="Person"
              initialId={form.person_id}
              initialName={form.person_name}
              placeholder="Search people…"
              onSelect={(id, name) => setForm((prev) => ({ ...prev, person_id: id, person_name: name }))}
              inputClass={ic}
            />
            {errors.person && <p className="mt-1 text-xs text-red-600">{errors.person}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-ink-muted mb-1">Joined year</label>
              <input name="joined_year" type="number" value={form.joined_year} onChange={handleChange} className={ic} />
            </div>
            <div>
              <label className="block text-sm text-ink-muted mb-1">Left year</label>
              <input name="left_year" type="number" value={form.left_year} onChange={handleChange} className={ic} />
            </div>
          </div>

          <div>
            <label className="block text-sm text-ink-muted mb-1">Importance</label>
            <input name="importance" type="number" value={form.importance} onChange={handleChange} className={ic} />
          </div>

          <div>
            <label className="block text-sm text-ink-muted mb-1">Details</label>
            <textarea name="details" rows={3} value={form.details} onChange={handleChange} className={ic} />
          </div>

          <div>
            <label className="block text-sm text-ink-muted mb-1">Instruments</label>
            <input name="instruments" type="text" value={form.instruments} onChange={handleChange} placeholder="e.g. guitar, vocals" className={ic} />
            <p className="mt-1 text-xs text-ink-muted">Comma-separated list of instruments</p>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Button type="submit">{isNew ? "Create member" : "Update member"}</Button>
            <Link href={cancelHref} className="text-sm text-ink-muted no-underline hover:text-ink-display">Cancel</Link>
          </div>
        </form>
      </PublicShell>
    </>
  )
}
